package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.tedd.db.Skill
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis

/** Compétences : ce qu'on apprend, mesuré en heures de focus. */
class SkillRepository(private val db: TeddDatabase) {

    fun observeAll(): Flow<List<Skill>> =
        db.skillQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    suspend fun create(
        name: String,
        color: String = "#22508A",
        icon: String = "book",
        intention: String? = null,
        targetHours: Long = 1000L,
        tags: String? = null,
        orderIndex: Long = 0L,
        id: String = newId("sk"),
        deadline: String? = null,
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.skillQueries.insert(
            id, name.trim(), color, icon, intention?.takeIf { it.isNotBlank() },
            targetHours, deadline?.takeIf { it.isNotBlank() }, tags?.takeIf { it.isNotBlank() }, 0L, 0L, orderIndex, now, now,
        )
        id
    }

    suspend fun update(
        id: String,
        name: String,
        color: String,
        icon: String,
        intention: String?,
        targetHours: Long,
        tags: String?,
        pinned: Boolean,
        archived: Boolean,
        orderIndex: Long,
        deadline: String?,
    ) = withContext(Dispatchers.Default) {
        db.skillQueries.update(
            name.trim(), color, icon, intention?.takeIf { it.isNotBlank() }, targetHours,
            deadline?.takeIf { it.isNotBlank() }, tags?.takeIf { it.isNotBlank() }, if (pinned) 1L else 0L,
            if (archived) 1L else 0L, orderIndex, nowMillis(), id,
        )
    }

    /** Épingle ou dépingle : le geste le plus fréquent mérite sa propre requête. */
    suspend fun setPinned(id: String, pinned: Boolean) = withContext(Dispatchers.Default) {
        db.skillQueries.setPinned(if (pinned) 1L else 0L, nowMillis(), id)
    }

    suspend fun softDelete(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.skillQueries.softDelete(now, now, id)
    }

    /** Rattache une course de focus à une compétence. */
    suspend fun tagFocusSession(sessionId: String, skillId: String?) = withContext(Dispatchers.Default) {
        db.focusSessionQueries.updateSkill(skillId, nowMillis(), sessionId)
    }

    /** Rattache un objectif à une compétence : le temps passé sur ses tâches y comptera. */
    suspend fun tagObjective(objectiveId: String, skillId: String?) = withContext(Dispatchers.Default) {
        db.objectiveQueries.updateSkill(skillId, nowMillis(), objectiveId)
    }

    /** Rattache un projet à une compétence : le temps passé à construire y comptera. */
    suspend fun tagProject(projectId: String, skillId: String?) = withContext(Dispatchers.Default) {
        db.projectQueries.updateSkill(skillId, nowMillis(), projectId)
    }
}
